import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaShoppingBag, FaChevronLeft, FaChevronRight, FaPlus, FaTimes } from 'react-icons/fa';
import { getProducts } from '../services/api';
import './NewArrivals.css';

const BADGE_CLASS_MAP = {
  'Sale': 'badge-sale',
  'New Arrival': 'badge-new',
  'Best Seller': 'badge-bestseller',
  'Limited': 'badge-limited',
};

const NewArrivals = () => {
  const [products, setProducts] = useState([]);
  const [sliderTitle, setSliderTitle] = useState('New Arrivals');
  const [sliderSubtitle, setSliderSubtitle] = useState('Fresh styles just added to the collection.');
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null); // desktop hover
  const [expandedId, setExpandedId] = useState(null); // mobile tap toggle
  const [sizesCache, setSizesCache] = useState({});
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    loadSliderSettings();
    loadFeaturedProducts();
  }, []);

  const loadSliderSettings = async () => {
    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/public/get-settings.php');
      const data = await response.json();
      if (data.success && data.settings) {
        if (data.settings.homepage_slider_title) setSliderTitle(data.settings.homepage_slider_title);
        if (data.settings.homepage_slider_subtitle) setSliderSubtitle(data.settings.homepage_slider_subtitle);
      }
    } catch (error) {
      console.error('Error loading slider settings:', error);
    }
  };

  const loadFeaturedProducts = async () => {
    try {
      const data = await getProducts();
      if (data.success && data.products) {
        const featured = data.products
          .filter(p => Number(p.featured) === 1)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setProducts(featured);
      }
    } catch (error) {
      console.error('Error loading featured products:', error);
    } finally {
      setLoading(false);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 280;
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const newScroll = direction === 'left'
        ? currentScroll - scrollAmount
        : currentScroll + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  const loadSizesForProduct = async (productId) => {
    if (sizesCache[productId]) return;

    setSizesCache(prev => ({ ...prev, [productId]: 'loading' }));
    try {
      const response = await fetch(`https://demo-site.storii.com.pk/demo-api/admin/get-product-guide.php?product_id=${productId}`);
      const data = await response.json();
      const rows = data?.guide?.guide_rows;
      if (data.success && Array.isArray(rows) && rows.length > 0) {
        const sizes = rows.map(r => r.size).filter(Boolean);
        setSizesCache(prev => ({ ...prev, [productId]: sizes.length > 0 ? sizes : 'none' }));
      } else {
        setSizesCache(prev => ({ ...prev, [productId]: 'none' }));
      }
    } catch (error) {
      console.error('Error loading sizes:', error);
      setSizesCache(prev => ({ ...prev, [productId]: 'none' }));
    }
  };

  const handleHoverStart = (productId) => {
    setHoveredId(productId);
    loadSizesForProduct(productId);
  };

  // Mobile "+" toggle — no hover available on touch, so tapping "+" opens
  // the same overlay hover would, and tapping again (now showing ✕) closes it.
  const handleToggleExpand = (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    if (expandedId === productId) {
      setExpandedId(null);
    } else {
      setExpandedId(productId);
      loadSizesForProduct(productId);
    }
  };

  const addToCart = (product, size = null) => {
    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    const existingItem = cart.find(item => item.id === product.id && item.size === size);

    const currentQtyInCart = existingItem ? existingItem.quantity : 0;
    const availableStock = product.stock !== undefined ? parseInt(product.stock) : 0;

    if (currentQtyInCart >= availableStock) {
      // You might need to import toast at the top if you use this here
      // toast.error(`Sorry, only ${availableStock} items available in stock.`);
      return;
    }

    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      cart.push({ ...product, size, quantity: 1 });
    }
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    window.dispatchEvent(new Event('openSideCart'));
    setExpandedId(null);
  };

  if (loading) {
    return (
      <div className="">
        <div className=""></div>
      </div>
    );
  }

  if (products.length === 0) {
    return null;
  }

  if (loading) {
    return null;
  }

  return (
    <div className="new-arrivals-page">
      <div className="new-arrivals-container">
        <div className="new-arrivals-header">
          <div>
            <h2 className="new-arrivals-title">{sliderTitle}</h2>
            <p className="new-arrivals-subtitle">{sliderSubtitle}</p>
          </div>
          <div className="slider-arrows">
            <button className="slider-arrow prev" onClick={() => scroll('left')} aria-label="Previous">
              <FaChevronLeft />
            </button>
            <button className="slider-arrow next" onClick={() => scroll('right')} aria-label="Next">
              <FaChevronRight />
            </button>
          </div>
        </div>

        <div className="products-slider" ref={scrollContainerRef}>
          {products.map((product) => {
            const sizeState = sizesCache[product.id];
            const hasRealSizes = Array.isArray(sizeState) && sizeState.length > 0;
            const isRevealed = hoveredId === product.id || expandedId === product.id;
            const isExpanded = expandedId === product.id;
            const isOutOfStock = !product.stock || Number(product.stock) <= 0;

            return (
              <div
                key={product.id}
                className="product-slide"
                onMouseEnter={() => !isOutOfStock && handleHoverStart(product.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <Link to={`/product/${product.id}`} className="product-link">
                  <div className="product-image-wrapper">
                    <img
                      src={product.image || 'https://via.placeholder.com/400x500'}
                      alt={product.name}
                      className="product-img"
                    />
                    {product.slider_badge && (
                      <span className={`badge ${BADGE_CLASS_MAP[product.slider_badge] || 'badge-new'}`}>
                        {product.slider_badge}
                      </span>
                    )}

                    {/* Out-of-Stock overlay badge */}
                    {isOutOfStock && (
                      <span className="out-of-stock-badge">Out of Stock</span>
                    )}

                    {/* Mobile-only Quick Add under price */}
                    <button
                      type="button"
                      className="mobile-quick-add-btn"
                      disabled={isOutOfStock}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isOutOfStock) return;
                        // ... your add to cart logic
                      }}
                    >
                      <FaShoppingBag className="cart-icon" /> {isOutOfStock ? 'Out of Stock' : 'Quick Add'}
                    </button>

                    {/* Only render hover overlay if in stock */}
                    {!isOutOfStock && (
                      <div className={`quick-add-overlay ${isRevealed ? 'visible' : ''}`}>
                        {hasRealSizes ? (
                          <>
                            <span className="quick-add-label"></span>
                            <div className="quick-add-sizes">
                              {sizeState.map(size => (
                                <button
                                  key={size}
                                  type="button"
                                  className="quick-add-size-btn"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    addToCart(product, size);
                                  }}
                                >
                                  {size}
                                </button>
                              ))}
                            </div>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="quick-add-plain-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              addToCart(product);
                            }}
                          >
                            <FaShoppingBag className="cart-icon" /> Quick Add
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <h3 className="product-name">{product.name}</h3>
                  <div className="product-price">
                    {product.sale_price ? (
                      <>
                        <span className="current-price">PKR {parseFloat(product.sale_price).toLocaleString()}</span>
                        <span className="old-price">PKR {parseFloat(product.price).toLocaleString()}</span>
                      </>
                    ) : (
                      <span className="current-price">PKR {parseFloat(product.price).toLocaleString()}</span>
                    )}
                  </div>
                </Link>

                {/* Mobile-only Quick Add under price — hidden on desktop via CSS */}
                <button
                  type="button"
                  className="mobile-quick-add-btn"
                  disabled={isOutOfStock}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isOutOfStock) return;
                    if (hasRealSizes) {
                      handleToggleExpand(e, product.id);
                    } else {
                      addToCart(product);
                    }
                  }}
                >
                  <FaShoppingBag className="cart-icon" /> {isOutOfStock ? 'Out of Stock' : 'Quick Add'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NewArrivals;